import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Spinner } from './utilities';

export interface ConfirmDialogProps {
  /** Whether the dialog is open (supports both `open` and `isOpen` for backwards compatibility) */
  open?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  /** Message can be a string or React node for complex content */
  message?: string;
  description?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Variant controls button styling. 'destructive' is an alias for 'danger' */
  variant?: 'default' | 'danger' | 'warning' | 'destructive';
  /** Show loading spinner on confirm button */
  loading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  loading = false,
}) => {
  // Support both `open` and `isOpen` props
  const isDialogOpen = open ?? isOpen ?? false;

  // Support both `message` and `description` props
  const content = description ?? message;

  const handleConfirm = async () => {
    await onConfirm();
  };

  // Normalize variant - 'destructive' is an alias for 'danger'
  const normalizedVariant = variant === 'destructive' ? 'danger' : variant;

  const buttonClass =
    normalizedVariant === 'danger'
      ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
      : normalizedVariant === 'warning'
      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
      : '';

  return (
    <AlertDialog open={isDialogOpen} onOpenChange={(dialogOpen) => !dialogOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {typeof content === 'string' ? (
            <AlertDialogDescription>{content}</AlertDialogDescription>
          ) : (
            <div className="text-sm text-muted-foreground">{content}</div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose} disabled={loading}>
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={buttonClass}
            disabled={loading}
          >
            {loading && <Spinner size="sm" className="mr-2" />}
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
